// @ts-nocheck
// Courts tab setup tools: copy courts forward from a prior event, and import
// courts from a pasted block or CSV. Both preview before anything is written,
// both are additive (nothing is ever deleted, no venue is ever created).
import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Copy, Loader2, Upload } from "lucide-react";
import { formatCalendarRange } from "@/lib/dates";

const actionBadge = (row: any) => {
  if (row.action === "create") return <Badge variant="secondary">New</Badge>;
  if (row.action === "update") return <Badge variant="outline">Updates existing</Badge>;
  return <Badge variant="destructive">Cannot import</Badge>;
};

function PreviewTable({ rows, showRowNumbers }: any) {
  if (!rows?.length) return <p className="text-sm text-muted-foreground">Nothing to import.</p>;
  return (
    <div className="max-h-72 overflow-y-auto rounded border">
      <Table>
        <TableHeader>
          <TableRow>
            {showRowNumbers && <TableHead className="w-12">Row</TableHead>}
            <TableHead>Court</TableHead>
            <TableHead>Venue</TableHead>
            <TableHead className="w-20">Order</TableHead>
            <TableHead>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r: any, i: number) => (
            <TableRow key={`${r.row ?? i}-${r.name}`}>
              {showRowNumbers && <TableCell className="text-muted-foreground">{r.row ?? "—"}</TableCell>}
              <TableCell className="font-medium">{r.name || <span className="text-muted-foreground">(blank)</span>}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {r.venue_label ?? (r.action === "skip" ? "—" : "Main venue")}
              </TableCell>
              <TableCell className="text-sm">
                {r.sort_order ?? "—"}
                {r.sort_order_appended && <span className="ml-1 text-xs text-muted-foreground">(appended)</span>}
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex items-center gap-2">
                  {actionBadge(r)}
                  {r.reason && <span className="text-xs text-muted-foreground">{r.reason}</span>}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Copy from another event                                             */
/* ------------------------------------------------------------------ */
function CopyCourtsDialog({ eventId, onDone }: any) {
  const [open, setOpen] = useState(false);
  const [sourceId, setSourceId] = useState("");
  const [copyAvailability, setCopyAvailability] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const sourcesQuery = useQuery({
    queryKey: ["copyable-courts", eventId],
    enabled: open && !!eventId,
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction("rle-list-copyable-courts", {
        body: { event_id: eventId },
      });
      if (error) throw new Error(error.message);
      if (data?.ok === false) throw new Error(data?.error?.message ?? "Could not load events");
      return data.data.sources ?? [];
    },
  });

  const call = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const { data, error } = await invokeEdgeFunction("rle-copy-courts-from-previous-event", {
        body: {
          target_event_id: eventId,
          source_event_id: sourceId,
          copy_availability: copyAvailability,
          dry_run: dryRun,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.ok === false) throw new Error(data?.error?.message ?? "Copy failed");
      if (dryRun) {
        setPreview(data.data.preview);
      } else {
        const r = data.data.result;
        toast.success(
          `${r.courts_created} court${r.courts_created === 1 ? "" : "s"} created, ${r.courts_updated} updated` +
          (r.availability_windows_copied ? `, ${r.availability_windows_copied} availability window(s) copied` : "") +
          (r.dropped_days?.length ? `. Dropped windows on ${r.dropped_days.join(", ")}` : "") +
          ". Nothing was deleted."
        );
        setOpen(false);
        setPreview(null);
        setSourceId("");
        onDone?.();
      }
    } catch (e: any) {
      toast.error(e.message ?? "Copy failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Copy className="mr-1 h-4 w-4" /> Copy from another event
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Copy courts from another event</DialogTitle>
            <DialogDescription>
              Court names, order, venue, notes and active state are copied. Nothing on this event is ever
              deleted, and existing courts keep their id, so placed games are untouched.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Source event</Label>
              {sourcesQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
                </div>
              ) : (
                <Select value={sourceId} onValueChange={(v) => { setSourceId(v); setPreview(null); }}>
                  <SelectTrigger><SelectValue placeholder="Choose an event…" /></SelectTrigger>
                  <SelectContent>
                    {(sourcesQuery.data ?? []).map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} — {s.court_count} court{s.court_count === 1 ? "" : "s"}, {s.venue_count} venue{s.venue_count === 1 ? "" : "s"}
                        {s.start_date ? ` · ${formatCalendarRange(s.start_date, s.end_date)}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {sourcesQuery.data?.length === 0 && (
                <p className="text-sm text-muted-foreground">No other event in this organization has courts yet.</p>
              )}
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="copy-availability"
                checked={copyAvailability}
                onCheckedChange={(v) => { setCopyAvailability(v === true); setPreview(null); }}
              />
              <div>
                <Label htmlFor="copy-availability" className="cursor-pointer">
                  Also copy that event's availability windows
                </Label>
                <p className="text-xs text-muted-foreground">
                  Mapped onto this event's dates in order — source day one becomes day one here.
                  If this event has fewer days, the extra windows are dropped.
                </p>
              </div>
            </div>

            {preview && (
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>{preview.create_count}</strong> to create,{" "}
                  <strong>{preview.update_count}</strong> to update,{" "}
                  <strong>{preview.skip_count}</strong> skipped
                  {preview.copy_availability && <> · <strong>{preview.window_count}</strong> availability window(s)</>}
                </p>
                {preview.availability_note && (
                  <p className="text-sm text-amber-600">{preview.availability_note}</p>
                )}
                {preview.renumber_note && (
                  <p className="text-xs text-muted-foreground">{preview.renumber_note}</p>
                )}
                <PreviewTable rows={preview.rows} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" disabled={!sourceId || busy} onClick={() => call(true)}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Preview
            </Button>
            <Button disabled={!preview || busy} onClick={() => call(false)}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Copy courts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Spreadsheet import                                                  */
/* ------------------------------------------------------------------ */
function ImportCourtsDialog({ eventId, onDone }: any) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const call = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const { data, error } = await invokeEdgeFunction("rle-import-courts", {
        body: { event_id: eventId, text, dry_run: dryRun },
      });
      if (error) throw new Error(error.message);
      if (data?.ok === false) throw new Error(data?.error?.message ?? "Import failed");
      if (dryRun) {
        setPreview(data.data.preview);
      } else {
        const r = data.data.result;
        toast.success(
          `${r.courts_created} created, ${r.courts_updated} updated, ${r.rows_skipped.length} row(s) skipped. Nothing was deleted.`
        );
        setOpen(false);
        setPreview(null);
        setText("");
        onDone?.();
      }
    } catch (e: any) {
      toast.error(e.message ?? "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
    setPreview(null);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Upload className="mr-1 h-4 w-4" /> Import courts
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import courts from a spreadsheet</DialogTitle>
            <DialogDescription>
              Paste rows or choose a CSV. A header row is required; column order is not.
              Columns: venue, court, sort_order (optional), notes (optional).
              A blank venue means this event's main venue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onFile} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                Choose CSV file
              </Button>
              <span className="text-xs text-muted-foreground">or paste below</span>
            </div>
            <Textarea
              rows={8}
              className="font-mono text-xs"
              placeholder={"venue,court,sort_order,notes\nWest Gym,Court 1,1,Scoreboard side\nWest Gym,Court 2,2,\n,Court 3,,Main venue"}
              value={text}
              onChange={(e) => { setText(e.target.value); setPreview(null); }}
            />

            {preview && (
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>{preview.create_count}</strong> to create,{" "}
                  <strong>{preview.update_count}</strong> to update,{" "}
                  <strong>{preview.skip_count}</strong> skipped
                  {preview.blank_name_rows > 0 && ` (${preview.blank_name_rows} with no court name)`}
                </p>
                {preview.renumber_note && (
                  <p className="text-xs text-muted-foreground">{preview.renumber_note}</p>
                )}
                <PreviewTable rows={preview.rows} showRowNumbers />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" disabled={!text.trim() || busy} onClick={() => call(true)}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Preview
            </Button>
            <Button disabled={!preview || preview.create_count + preview.update_count === 0 || busy} onClick={() => call(false)}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CourtSetupTools({ eventId, onDone }: any) {
  return (
    <div className="flex items-center gap-2">
      <CopyCourtsDialog eventId={eventId} onDone={onDone} />
      <ImportCourtsDialog eventId={eventId} onDone={onDone} />
    </div>
  );
}
